import React, { useEffect, useState, useRef } from "react";
import useMainContentDimensions from "../../../utils/mainContentDimensions";
import {
  useTheme,
  useMediaQuery,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import useWindowDimensions from "../../../utils/windowDimensions";
import useAgreementTracker from "../../../hooks/useAgreementTracker";
import useAuth from "../../../hooks/useAuth";
import { formatDate, addDays } from "../../../utils/common";

function AED() {
  const theme = useTheme();

  // Media queries for responsiveness
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Tablet devices
  const isLaptopM = useMediaQuery(theme.breakpoints.between("md", "lg")); // Laptop Medium (1280px–1440px)
  const isLaptopL = useMediaQuery(theme.breakpoints.up("lg")); // Laptop Large (1440px and above)

  const { user } = useAuth();
  const { width: mainContentWidth } = useMainContentDimensions();

  const currentFilters = [
    {
      fieldName: "dateOfExpiry",
      fieldValue:
        formatDate(addDays(new Date(), 30)) +
        " 00:00:00," +
        formatDate(addDays(new Date(), 30)) +
        " 23:59:59",
      op: "btn",
      dataType: "string",
      condition: "and",
      isJoin: false,
    },
  ];

  if (user && user.businessType !== "SUPER_ADMIN" && user.tenantId) {
    currentFilters.push({
      fieldName: user.businessType === "MULTI_SERVICE_OPERATOR" ? "msoTenantId" : "tenantId",
      fieldValue: user.tenantId,
      op: "eq",
      dataType: "long",
      condition: "and",
      isJoin: true,
    });
  }

  const currentPayload = {
    filters: currentFilters,
    pagination: {
      pageNumber: 1,
      pageSize: 5,
    },
    sortFields: [
      {
        field: "createdAt",
        sort: "DESC",
      },
    ],
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 5,
  });

  const [sortModel, setSortModel] = useState([
    {
      field: "updatedAt",
      sort: "desc",
    },
  ]);

  const { height } = useWindowDimensions();
  const { getAllAgreementTracker, agreementTrackers, paging, isLoading } = useAgreementTracker();

  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;

  const [payload, setPayload] = useState(currentPayload);

  const fetchData = async () => {
    if (Object.keys(payload).length !== 0) {
      await getAllAgreementTracker(payload);
    }
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");
    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: payload.pagination.pageSize,
    };
    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  useEffect(() => {
    const fetchRealTimeData = async () => {
      const filters = [];
      if (user && user.businessType !== "SUPER_ADMIN" && user.tenantId) {
        filters.push({
          fieldName: user.businessType === "MULTI_SERVICE_OPERATOR" ? "msoTenantId" : "tenantId",
          fieldValue: user.tenantId,
          op: "eq",
          dataType: "long",
          condition: "and",
          isJoin: true,
        });
      }

      // Show agreements expiring between TODAY and 30 DAYS from now
      const startDate = formatDate(new Date());
      const endDate = formatDate(addDays(new Date(), 30));

      filters.push({
        fieldName: "dateOfExpiry",
        fieldValue: `${startDate} 00:00:00,${endDate} 23:59:59`,
        op: "btn",
        dataType: "string",
        condition: "and",
        isJoin: false,
      });

      const newPayload = {
        filters,
        pagination: { pageNumber: 1, pageSize: 5 },
        sortFields: [{ fieldName: "dateOfExpiry", sortOrder: "ASC" }],
      };

      setPayload(newPayload);
      await getAllAgreementTracker(newPayload);
    };

    if (user) {
      fetchRealTimeData();
    }
  }, [user]);

  useEffect(() => {
    if (payload !== currentPayload) {
      fetchData();
    }
  }, [payload]);

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  const columns = [
    {
      field: "tenant",
      headerName: "Franchise Name",
      valueGetter: (params) => params.tenantName,
      width: isTablet ? 150 : isLaptopM ? 200 : isLaptopL ? 220 : 180, // Adjust column width
    },
    {
      field: "dateOfExpiryStr",
      headerName: "Agreement Expiry Date",
      width: isTablet ? 140 : isLaptopM ? 180 : isLaptopL ? 200 : 160, // Adjust column width
    },
  ];

  const paginationMetaRef = useRef();

  // Memoize to avoid flickering when the `hasNextPage` is `undefined` during refetch
  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  return (
    <Grid item xs={12} md={6}>
      <Card
        sx={{
          width: "100%",
          borderRadius: "20px",
          backgroundColor: "#ffffff",
          boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          border: "none",
        }}
      >
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b" }}>
              Agreement Expiry Details
            </Typography>
          </Box>
          <Box sx={{ height: 420 }}>
            <DataGrid
              autoHeight
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } },
              }}
              columns={columns}
              rows={agreementTrackers}
              getRowId={(row) => row["agreementTrackerId"]}
              rowCount={paging?.totalNumberOfRecords}
              loading={isLoading}
              rowsPerPageOptions={[5]}
              pagination
              page={paging?.pageNumber}
              pageSize={5}
              paginationMode="server"
              onPaginationModelChange={onPageChange}
              paginationMeta={paginationMeta}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              paginationModel={paginationModel}
              getRowHeight={() => 64}
              slots={{
                noRowsOverlay: () => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontSize: "24px", opacity: 0.5 }}
                        role="img"
                        aria-label="calendar"
                      >
                        📅
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                      No agreement expiry found
                    </Typography>
                  </Box>
                ),
              }}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid #f1f5f9",
                  color: "#64748b",
                  fontSize: "12px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f1f5f9",
                  fontSize: "14px",
                  color: "#1e293b",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f8fafc",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "none",
                },
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
              }}
            />
          </Box>
        </Box>
      </Card>
    </Grid>
  );
}

export default AED;
